<template>
    <form
        id="productListFilterForm"
        class="product-list-filter-form"
        :style="{ 'margin-top': floatMarginTop }"
        @submit.prevent="submit"
    >
        <button
            v-if="mobile"
            type="button"
            class="toggle-product-filter-btn"
            @click="openMobile = !openMobile"
        >
            {{ labels.frontendProductList.filters.openFilter }}
        </button>
        <div
            :class="{
                'product-filter-column noTextSelect': true,
                open: openMobile,
            }"
        >
            <mobileFilter
                v-if="mobile"
                ref="mobileFilterCompInstance"
            ></mobileFilter>
            <sorting-comp
                v-if="
                    sorting != null &&
                    sorting.options != null &&
                    sorting.options.length > 0
                "
                v-model="listState.query.sorting"
                :options="sorting.options"
                @submitForm="submit()"
            ></sorting-comp>
            <Filter-comp
                :product-list-guid="productListGuid"
                :view-name="viewName"
                :view-id="viewId"
                :matched-logical-name="matchedLogicalName"
                :matched-logical-name-module-guid="matchedLogicalNameModuleGuid"
                :matched-logical-name-sub-module-guid="
                    matchedLogicalNameSubModuleGuid
                "
                :matched-logical-name-entity-guid="matchedLogicalNameEntityGuid"
                :product-group="productGroup"
                :region-set-filter="regionSetFilter"
                :single-geolocation-filter="singleGeolocationFilter"
                :collection-filter="collectionFilter"
                v-model="listState.query"
                @change-filter="submit()"
                :url-resolver="urlResolver"
                :list-state="listState"
            ></Filter-comp>
            <button
                type="button"
                v-if="mobile"
                data-keep-in-form
                class="product-list-filter-show-products-btn"
                @click="closeMobileFilter()"
            >
                Vis produkter
            </button>
        </div>
    </form>
</template>

<script setup lang="ts">
import {
    type PropType,
    computed,
    getCurrentInstance,
    onMounted,
    ref,
    onBeforeUnmount,
} from "vue";
import FilterComp from "./Filter.vue";
import {
    type RegionSetFilterInfo,
    type SortingOptionEnumString,
} from "./types";
import { getProductListState } from "./product-list-state";
import type { UrlResolver } from "./seo-url-parser";
import sortingComp from "./sorting/sorting.vue";
import { type SortingOptionItem } from "./sorting/types";
import mobileFilter from "./mobile-filter-container/mobile-filter.vue";
import { isMobile } from "./product-list/mobile-detect";
import labels from "../shared/labels";

const mobile = ref(isMobile());
const openMobile = ref(false);
const floatMarginTopValue = ref<number>();
const floatMarginTop = computed(() => {
    if (floatMarginTopValue.value == null) return undefined;
    return floatMarginTopValue.value + "px";
});

const mobileFilterCompInstance = ref<InstanceType<typeof mobileFilter>>();

function closeMobileFilter() {
    if (mobileFilterCompInstance.value != null) {
        (
            mobileFilterCompInstance.value as unknown as {
                setOpen(v: boolean): void;
            }
        ).setOpen(false);
    }
}

const props = defineProps({
    urlResolver: {
        type: Object as PropType<UrlResolver>,
        required: true,
    },
    productListGuid: Number,
    productFilterId: {
        type: String,
        required: true,
    },
    viewName: String,
    viewId: String,
    matchedLogicalName: String,
    matchedLogicalNameModuleGuid: Number,
    matchedLogicalNameSubModuleGuid: Number,
    matchedLogicalNameEntityGuid: Number,
    productGroup: Object,
    regionSetFilter: Object as PropType<RegionSetFilterInfo>,
    singleGeolocationFilter: Object,
    collectionFilter: Object as PropType<{
        collectionIdName: string;
        enabled: boolean;
        viewMode: number;
    }>,
    sorting: Object as PropType<{
        options: SortingOptionItem[];
        initialValue: SortingOptionEnumString;
    }>,
});

const listState = getProductListState(props.productFilterId);

if (props.sorting != null) {
    listState.value.query.sorting = props.sorting.initialValue;
}

listState.value.mln = {
    matchedLogicalName: props.matchedLogicalName,
    matchedLogicalNameModuleGuid: props.matchedLogicalNameModuleGuid,
    matchedLogicalNameSubModuleGuid: props.matchedLogicalNameSubModuleGuid,
};

async function submit() {
    // console.log(listState.value.list);
    listState.value.loading = true;
    if (listState.value.list != null) {
        listState.value.list.submit(true);
    } else {
        console.warn(
            "Tried to call listState list submit but value is still not set! Setting submitListWhenReady"
        );
        listState.value.whenListReady = function (list) {
            list.submit(true);
        };
    }
}

let intervalId: number | undefined;
const instance = getCurrentInstance();

onBeforeUnmount(() => {
    if (intervalId != null && intervalId >= 0) {
        clearInterval(intervalId);
    }
});

function getAbsoluteTop(el: HTMLElement) {
    return el.getBoundingClientRect().top;
    // let top = 0;
    // while (el != null) {
    //     top += el.offsetTop;
    //     el = el.parentElement;
    // }
    // return top;
}

function hasTop(el: HTMLElement | undefined | null) {
    if (
        el == null ||
        el.ownerDocument == null ||
        getComputedStyle(el).display == "none"
    )
        return false;
    return true;
}

let topDescEl: HTMLDivElement | undefined;
let $el: HTMLFormElement | undefined;
let productList: HTMLFormElement | undefined;

const logging = false;
const log = logging
    ? (...args: Parameters<typeof console.log>) => console.log(...args)
    : // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (...args: Parameters<typeof console.log>) => {};

type ElementDescriptor<Element = HTMLElement> = {
    find: () => Element | null;
    check(el: Element): boolean;
};

const elementDescriptors = {
    topDescEl: {
        check(el) {
            return (
                el.getAttribute("data-role") == "product-list-top-description"
            );
        },
        find() {
            return document.querySelector<HTMLDivElement>(
                "[data-role='product-list-top-description']"
            );
        },
    } as ElementDescriptor<HTMLDivElement>,
    productList: {
        check(el) {
            return (
                el.classList != null &&
                el.classList.contains("async-product-list")
            );
        },
        find() {
            return document.querySelector<HTMLDivElement>(
                ".async-product-list"
            );
        },
    } as ElementDescriptor<HTMLFormElement>,
    $el: {
        check(el) {
            return (
                el.classList != null &&
                el.classList.contains("product-list-filter-form")
            );
        },
        find() {
            return instance?.proxy.$el;
        },
    } as ElementDescriptor<HTMLFormElement>,
};

const addedElementObserver = new MutationObserver((entries) => {
    const insertedElements = entries
        .filter((x) => {
            if (x.type == "childList" && x.addedNodes.length > 0) return true;
            if (x.type == "attributes") return true;
            return false;
        })
        .flatMap((x) =>
            x.type == "childList" ? Array.from(x.addedNodes) : [x.target]
        );
    let hasChanged = false;
    for (const insertedElement of insertedElements) {
        if (
            topDescEl == null &&
            insertedElement instanceof HTMLDivElement &&
            elementDescriptors.topDescEl.check(insertedElement)
        ) {
            hasChanged = true;
            topDescEl = insertedElement;
        } else if (
            productList == null &&
            insertedElement instanceof HTMLFormElement &&
            elementDescriptors.productList.check(insertedElement)
        ) {
            hasChanged = true;
            productList = insertedElement;
        }
    }
    if (hasChanged) {
        log("OBS: RUNNING FROM OBSERVER");
        checkTopAlignment();
    } else {
        log(
            "OBS: inserted " +
                insertedElements.length +
                " elements but none assigned"
        );
    }
    if (topDescEl != null && productList != null) {
        addedElementObserver.disconnect();
    }
});

function checkTopAlignment() {
    if (topDescEl == null || topDescEl.ownerDocument == null) {
        topDescEl = elementDescriptors.topDescEl.find() ?? undefined;
        if (logging) {
            if (topDescEl == null) {
                log("topDesc el not found yet");
            } else {
                log("topDesc el found");
            }
        }
    }
    if (productList == null || productList.ownerDocument == null) {
        productList = elementDescriptors.productList.find() ?? undefined;
        if (logging) {
            if (topDescEl == null) {
                log("productList el not found yet");
            } else {
                log("productList el found");
            }
        }
    }
    if ($el == null || $el.ownerDocument == null) {
        $el = instance?.proxy.$el as HTMLFormElement;
        if (logging) {
            if (topDescEl == null) {
                log("component el not found yet");
            } else {
                log("component el found");
            }
        }
    }
    if ($el == null) {
        log("Cancel: no component element");
        return;
    }
    const listTop = [topDescEl, productList].find((x) => hasTop(x));
    if (listTop == null) {
        console.warn("bail: all null");
        return;
    }
    const listTopY = getAbsoluteTop(listTop);
    const selfY = getAbsoluteTop($el);

    const diff = listTopY - selfY;

    if (Math.abs(diff) < 2) {
        log("bail: too low diff");
        return;
    }

    if (Math.abs(diff) > 200) {
        log("bail: too high diff: " + diff);
        return;
    }

    const newDiff = (floatMarginTopValue.value ?? 0) + diff;
    const topKey = listTop === topDescEl ? "top-desc" : "list-top";
    log(`diff between self ${selfY} and top list (${topKey}) ${listTopY}`);
    log(`diff from ${floatMarginTopValue.value} to ${newDiff}`);
    floatMarginTopValue.value = newDiff;
}

const cancelLoadEvents = new AbortController();

if (!mobile.value) {
    window.addEventListener("DOMContentLoaded", () => checkTopAlignment(), {
        once: true,
        signal: cancelLoadEvents.signal,
    });
    window.addEventListener("load", () => checkTopAlignment(), {
        once: true,
        signal: cancelLoadEvents.signal,
    });
}

onMounted(() => {
    cancelLoadEvents.abort();
    if (!mobile.value) {
        checkTopAlignment();
        // intervalId = window.setInterval(() => {
        //     checkTopAlignment();
        // }, 1000);
        addedElementObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributeFilter: ["class", "data-role"],
            attributes: true,
        });
    }
});
</script>
